import { Button, DatePicker, Input, message, Table } from "antd";
import { useState, useContext } from "react";
import { AuthContext } from "../../context/AuthContext";
import axios from "axios";
import dayjs from "dayjs";

const { TextArea } = Input;

export default function EditAttendance({ employee }) {
    const [givenDate, setGivenDate] = useState(null);
    const [record, setRecord] = useState(null);
    const [loading, setLoading] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const [attType, setAttType] = useState("");
    const [inType, setInType] = useState("");

    const { authData, backUrl } = useContext(AuthContext);

    const [payload, setPayload] = useState({
        attendance_id: "",
        date: "",
        first_in_type: "",
        first_in_value: "",
        last_out_type: "",
        last_out_value: "",
        reason: "",
    });

    const activeStyle = {
        backgroundColor: "#0e497b",
        borderColor: "#0e497b",
        color: "#fff",
    };

    const toggleBtnStyle = (isActive) => ({
        height: "40px",
        fontWeight: 500,
        borderRadius: "6px",
        border: isActive ? "2px solid #0e497b" : "1px solid #d9d9d9",
        backgroundColor: isActive ? "#e8f0fe" : "#fff",
        color: isActive ? "#0e497b" : "#555",
        boxShadow: isActive ? "0 0 0 2px rgba(14,73,123,0.15)" : "none",
        transition: "all 0.2s ease",
        cursor: "pointer",
        minWidth: "130px",
        padding: "0 16px",
        fontSize: "14px",
    });

    const fetchAttendanceRecord = async () => {
        if (!givenDate) {
            message.warning("Please select a date first.");
            return;
        }
        setLoading(true);
        try {
            const res = await axios.get(
                `${backUrl}/api/hr/attendance/?from_date=${givenDate}&to_date=${givenDate}&employee_id=${employee?.id}`,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );

            const data = res.data?.results?.[0];
            console.log("Attendance Record:", data);
            setRecord(data);
            setAttType("");
            setInType("");

            // Reset payload — only attendance_id and date are pre-set
            setPayload({
                attendance_id: data?.id ?? "",
                date: data?.Date ?? givenDate,
                first_in_type: "",
                first_in_value: "",
                last_out_type: "",
                last_out_value: "",
                reason: "",
            });
        } catch (error) {
            console.error("Error fetching attendance record:", error);
            message.error("Failed to fetch attendance record.");
        } finally {
            setLoading(false);
        }
    };

    const handleInTypeSelect = (type) => {
        setInType(type);
        // Set only the type in payload, clear time so user must pick it
        if (type === "CHECK_IN" || type === "PUNCH_IN") {
            setPayload((p) => ({
                ...p,
                first_in_type: type,
                first_in_value: "",
                // Clear last out fields — not being edited
                last_out_type: "",
                last_out_value: "",
            }));
        } else if (type === "CHECK_OUT" || type === "PUNCH_OUT") {
            setPayload((p) => ({
                ...p,
                last_out_type: type,
                last_out_value: "",
                // Clear first in fields — not being edited
                first_in_type: "",
                first_in_value: "",
            }));
        }
    };

    const handleAttTypeSelect = (type) => {
        setAttType(type);
        setInType("");
        // Clear all type/time fields when switching
        setPayload((p) => ({
            ...p,
            first_in_type: "",
            first_in_value: "",
            last_out_type: "",
            last_out_value: "",
        }));
    };

    const handleSubmit = async () => {
        if (!payload.attendance_id && payload.attendance_id !== 0) {
            message.warning("No valid attendance record to update.");
            return;
        }
        if (!payload.reason) {
            message.warning("Please provide a reason.");
            return;
        }

        const isFirstIn = inType === "CHECK_IN" || inType === "PUNCH_IN";
        const isLastOut = inType === "CHECK_OUT" || inType === "PUNCH_OUT";

        if (isFirstIn && (!payload.first_in_type || !payload.first_in_value)) {
            message.warning("Please fill in the first-in details.");
            return;
        }
        if (isLastOut && (!payload.last_out_type || !payload.last_out_value)) {
            message.warning("Please fill in the last-out details.");
            return;
        }

        // Build minimal payload — only send what was edited
        const submitPayload = {
            attendance_id: payload.attendance_id,
            date: payload.date,
            reason: payload.reason,
        };

        if (isFirstIn) {
            submitPayload.first_in_type = payload.first_in_type;
            submitPayload.first_in_value = payload.first_in_value;
        }
        if (isLastOut) {
            submitPayload.last_out_type = payload.last_out_type;
            submitPayload.last_out_value = payload.last_out_value;
        }

        console.log("Submitting payload:", submitPayload);

        setSubmitting(true);
        try {
            const res = await axios.post(
                `${backUrl}/api/hr/regularization/apply/`,
                submitPayload,
                { headers: { Authorization: `Bearer ${authData?.access}` } }
            );
            console.log("Regularization Response:", res.data);
            message.success(res.data?.message || "Regularization submitted successfully!");
        } catch (error) {
            console.error("Submit error:", error);
            message.error("Failed to submit regularization.");
        } finally {
            setSubmitting(false);
        }
    };

    const recordColumns = [
        { title: "Employee Code", dataIndex: "EmployeeCode", key: "EmployeeCode" },
        { title: "Employee Name", dataIndex: "EmployeeName", key: "EmployeeName" },
        { title: "First In Type", dataIndex: "FirstInType", key: "FirstInType", render: (val) => val || "-" },
        { title: "First In Time", dataIndex: "FirstInTime", key: "FirstInTime", render: (val) => val || "-" },
        { title: "Last Out Type", dataIndex: "LastOutType", key: "LastOutType", render: (val) => val || "-" },
        { title: "Last Out Time", dataIndex: "LastOutTime", key: "LastOutTime", render: (val) => val || "-" },
        {
            title: "Attendance Status",
            dataIndex: "AttendanceStatus",
            key: "AttendanceStatus",
            render: (val) => (
                <span style={{
                    fontWeight: 500,
                    color: val === "ABSENT" ? "red" : val === "PRESENT" ? "green" : "#faad14",
                }}>
                    {val || "-"}
                </span>
            ),
        },
    ];

    return (
        <div style={{ padding: "24px", maxWidth: "900px", margin: "0 auto" }}>
            <h3 style={{ textAlign: "center", marginBottom: "20px" }}>Edit Improper Attendance</h3>

            {/* Date Picker + Fetch */}
            <div style={{ display: "flex", gap: "10px", marginBottom: "20px" }}>
                <DatePicker
                    style={{ flex: 1 }}
                    onChange={(_, dateString) => setGivenDate(dateString)}
                    placeholder="Select Date"
                />
                <Button style={activeStyle} onClick={fetchAttendanceRecord} loading={loading}>
                    Get Record
                </Button>
            </div>

            {record && (
                <div style={{ display: "flex", flexDirection: "column", gap: "24px" }}>

                    {/* Fetched Record Table */}
                    <Table
                        columns={recordColumns}
                        dataSource={[{ key: 1, ...record }]}
                        pagination={false}
                        scroll={{ x: true }}
                        bordered
                        components={{
                            header: {
                                cell: (props) => (
                                    <th {...props} style={{ backgroundColor: "#0e497b", color: "white", whiteSpace: "nowrap" }}>
                                        {props.children}
                                    </th>
                                ),
                            },
                        }}
                    />

                    {/* Step 1: First In / Last Out */}
                    <div style={{ textAlign: "center" }}>
                        <p style={{ fontWeight: 600, marginBottom: "12px", color: "#333" }}>
                            Select Type
                        </p>
                        <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
                            <button
                                style={toggleBtnStyle(attType === "First-In")}
                                onClick={() => handleAttTypeSelect("First-In")}
                            >
                                🕐 First In
                            </button>
                            <button
                                style={toggleBtnStyle(attType === "Last-Out")}
                                onClick={() => handleAttTypeSelect("Last-Out")}
                            >
                                🕔 Last Out
                            </button>
                        </div>
                    </div>

                    {/* Step 2: Check / Punch Mode */}
                    {attType === "First-In" && (
                        <div style={{ textAlign: "center" }}>
                            <p style={{ fontWeight: 600, marginBottom: "12px", color: "#333" }}>
                                Select Mode
                            </p>
                            <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
                                <button
                                    style={toggleBtnStyle(inType === "CHECK_IN")}
                                    onClick={() => handleInTypeSelect("CHECK_IN")}
                                >
                                    Check In
                                </button>
                                <button
                                    style={toggleBtnStyle(inType === "PUNCH_IN")}
                                    onClick={() => handleInTypeSelect("PUNCH_IN")}
                                >
                                    Punch In
                                </button>
                            </div>
                        </div>
                    )}

                    {attType === "Last-Out" && (
                        <div style={{ textAlign: "center" }}>
                            <p style={{ fontWeight: 600, marginBottom: "12px", color: "#333" }}>
                                Select Mode
                            </p>
                            <div style={{ display: "flex", gap: "12px", justifyContent: "center", flexWrap: "wrap" }}>
                                <button
                                    style={toggleBtnStyle(inType === "CHECK_OUT")}
                                    onClick={() => handleInTypeSelect("CHECK_OUT")}
                                >
                                    Check Out
                                </button>
                                <button
                                    style={toggleBtnStyle(inType === "PUNCH_OUT")}
                                    onClick={() => handleInTypeSelect("PUNCH_OUT")}
                                >
                                    Punch Out
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Step 3: Edit Form — only edited fields */}
                    {inType && (
                        <div style={{
                            maxWidth: "480px",
                            margin: "0 auto",
                            width: "100%",
                            display: "flex",
                            flexDirection: "column",
                            gap: "16px",
                        }}>
                            <p style={{ fontWeight: 600, color: "#333", marginBottom: "0" }}>
                                Edit Details
                            </p>

                            {/* First In Time only */}
                            {(inType === "CHECK_IN" || inType === "PUNCH_IN") && (
                                <div>
                                    <label style={{ fontWeight: 500, display: "block", marginBottom: "4px" }}>
                                        First In Time
                                    </label>
                                    <DatePicker
                                        showTime
                                        style={{ width: "100%" }}
                                        value={
                                            payload.first_in_value && dayjs(payload.first_in_value).isValid()
                                                ? dayjs(payload.first_in_value)
                                                : null
                                        }
                                        onChange={(date, dateString) =>
                                            setPayload((p) => ({ ...p, first_in_value: dateString }))
                                        }
                                        format="YYYY-MM-DD HH:mm:ss"
                                        placeholder="Select first in time"
                                    />
                                </div>
                            )}

                            {/* Last Out Time only */}
                            {(inType === "CHECK_OUT" || inType === "PUNCH_OUT") && (
                                <div>
                                    <label style={{ fontWeight: 500, display: "block", marginBottom: "4px" }}>
                                        Last Out Time
                                    </label>
                                    <DatePicker
                                        showTime
                                        style={{ width: "100%" }}
                                        value={
                                            payload.last_out_value && dayjs(payload.last_out_value).isValid()
                                                ? dayjs(payload.last_out_value)
                                                : null
                                        }
                                        onChange={(date, dateString) =>
                                            setPayload((p) => ({ ...p, last_out_value: dateString }))
                                        }
                                        format="YYYY-MM-DD HH:mm:ss"
                                        placeholder="Select last out time"
                                    />
                                </div>
                            )}

                            {/* Reason */}
                            <div>
                                <label style={{ fontWeight: 500, display: "block", marginBottom: "4px" }}>
                                    Reason <span style={{ color: "red" }}>*</span>
                                </label>
                                <TextArea
                                    rows={3}
                                    placeholder="Enter reason for changing"
                                    value={payload.reason}
                                    onChange={(e) => setPayload((p) => ({ ...p, reason: e.target.value }))}
                                />
                            </div>

                            {/* Submit */}
                            <Button
                                type="primary"
                                style={{ ...activeStyle, height: "42px", fontWeight: 600, borderRadius: "6px" }}
                                onClick={handleSubmit}
                                loading={submitting}
                                block
                            >
                                Edit Attendance
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
}